import {
  type IEngineRuntimeSection,
  type IEngineSection,
} from '@framework/engine/schema/sections/IEngineSection.ts';
import React, { createContext, useMemo, useState } from 'react';

export interface IModuleFormSectionsMonitorContext {
  runtimeSections: IEngineRuntimeSection[];
  setRuntimeSections: React.Dispatch<
    React.SetStateAction<IEngineRuntimeSection[]>
  >;
}

export const ModuleFormSectionsMonitorContext =
  createContext<IModuleFormSectionsMonitorContext | null>(null);

export function useModuleFormSectionsMonitorContext() {
  const context = React.useContext(ModuleFormSectionsMonitorContext);
  if (!context) {
    throw new Error(
      'useModuleFormSectionsMonitorContext must be used within a ModuleFormSectionsMonitorContext.Provider',
    );
  }
  return context;
}

/**
 * Ottiene le informazioni di runtime di una sezione del modulo.
 * Ad esempio, se è visibile e abilitata.
 */
export function useModuleFormSectionRuntime(sectionId: string) {
  const { runtimeSections } = useModuleFormSectionsMonitorContext();
  return useMemo(
    () => runtimeSections.find(s => s.section.id === sectionId),
    [runtimeSections, sectionId],
  );
}

/**
 * Indica se una sezione del modulo va renderizzata: richiede che la sezione
 * abbia elementi e che la regola di visibilità runtime sia verificata.
 *
 * Condizione unica condivisa tra `FillableForm` (contenuto della sezione) e
 * `SharedSectionVisibilityGate` (box grafico delle sezioni condivise), così
 * box e contenuto compaiono e spariscono sempre insieme.
 */
export function useModuleFormSectionVisible(section: IEngineSection): boolean {
  const runtimeInfo = useModuleFormSectionRuntime(section.id);
  return Boolean(section.ultimaVersione?.elementi && runtimeInfo?.visible);
}

interface IModuleFormSectionsMonitorContextProviderProps {
  children: React.ReactNode;
}

/**
 * Provider per il context di monitoraggio delle sezioni del modulo.
 */
export function ModuleFormSectionsMonitorContextProvider(
  props: IModuleFormSectionsMonitorContextProviderProps,
) {
  const [runtimeSections, setRuntimeSections] = useState<
    IEngineRuntimeSection[]
  >(() => []);

  const contextValue = useMemo(
    () => ({ runtimeSections, setRuntimeSections }),
    [runtimeSections],
  );

  return (
    <ModuleFormSectionsMonitorContext.Provider value={contextValue}>
      {props.children}
    </ModuleFormSectionsMonitorContext.Provider>
  );
}
